import { FC, useEffect, useState } from 'react';
import { API } from '@xbto/api-client';
import { EnrichedApiKey } from 'common';
import { AppDialog } from 'common';
import { DataStore } from '../../store';
import { CreateApiKey } from '../apikeys/add';
import { DeleteApiKey } from '../apikeys/delete';
import { ShowApiKey } from '../apikeys/show';
import {
  Button,
  NoApiKeysCallout,
  TextTitle,
} from '@xbto/universal-components';

interface RowProps {
  apiKeys: EnrichedApiKey[] | null;
  onShow: (key: string) => void;
}

const ApiKeys: FC<RowProps> = ({ apiKeys, onShow }) => {
  const keys = apiKeys || [];
  return (
    <>
      {keys.map(apiKey => {
        return (
          <div
            key={apiKey.key}
            className="flex flex-row"
            onClick={() => onShow(apiKey.key!)}
          >
            {apiKey.name}
          </div>
        );
      })}
    </>
  );
};

export const SettingsApiKeysV2: FC = () => {
  /**
   * Store
   */
  const apiKeys = DataStore.useStoreState(s => s.settings.apiKeys);
  const getApiKeys = DataStore.useStoreActions(a => a.settings.getApiKeys);
  const setError = DataStore.useStoreActions(a => a.setError);

  /**
   * State
   */
  const [showAddApiKeyDialog, setShowAddApiKeyDialog] = useState(false);
  const [toDeleteApiKeyId, setToDeleteApiKeyId] = useState<string | null>(null);
  const [toShowApiKey, setToShowApiKey] = useState<
    API.CreateApiKeyResponse | EnrichedApiKey | null
  >(null);

  /**
   * Hooks
   */
  useEffect(() => {
    getApiKeys();
  }, []);
  useEffect(() => {
    if (!toDeleteApiKeyId) {
      setError(null);
      getApiKeys();
    }
  }, [toDeleteApiKeyId]);

  /**
   * DOM
   */

  const isEmpty = (apiKeys || []).length === 0;

  return (
    <div className="">
      <div className="flex flex-row items-center justify-between p-6">
        <TextTitle>Api keys</TextTitle>
        <Button
          variant="secondary"
          size="base"
          label="Create API Key"
          onPress={() => {
            setShowAddApiKeyDialog(true);
          }}
        />
      </div>
      <section className="flex flex-col items-center">
        {/* table  */}
        <main>
          {isEmpty && (
            <NoApiKeysCallout onCtaPress={() => setShowAddApiKeyDialog(true)} />
          )}
          {!isEmpty && (
            <ApiKeys
              apiKeys={apiKeys}
              onShow={key =>
                setToShowApiKey(apiKeys?.find(i => i.key == key) || null)
              }
            />
          )}
        </main>
      </section>

      {/* add api key dialog  */}
      <AppDialog
        isOpen={showAddApiKeyDialog}
        onClose={() => {
          setShowAddApiKeyDialog(false);
        }}
      >
        <CreateApiKey
          onClose={(apiKey: API.CreateApiKeyResponse | null) => {
            setShowAddApiKeyDialog(false);
            setToShowApiKey(apiKey);
          }}
        />
      </AppDialog>

      {/* delete api key dialog  */}
      <AppDialog
        isOpen={toDeleteApiKeyId !== null}
        onClose={() => setToDeleteApiKeyId(null)}
      >
        <DeleteApiKey
          apiKeyId={toDeleteApiKeyId as string}
          onClose={() => setToDeleteApiKeyId(null)}
        />
      </AppDialog>

      {/* view api key dialog  */}
      <AppDialog
        isOpen={!!toShowApiKey}
        onClose={() => setToShowApiKey(null)}
        widthCls="max-w-2xl"
      >
        <ShowApiKey
          apiKey={toShowApiKey!}
          onClose={() => setToShowApiKey(null)}
          onDelete={apiKey => setToDeleteApiKeyId(apiKey)}
        />
      </AppDialog>
    </div>
  );
};
